import { Button, SpaceBetween, Spinner } from "@cloudscape-design/components";
import { renderPowerBy } from "../../../../components/PowerBy";
import { renderProviderLogo } from "../../../../components/ProviderLogo";
import { TextInput } from "../../../../components/TextInput";
import { DropdownBox } from "../../../../components/DropdownBox";
import PhoneInput from 'react-phone-input-2'
import { Multiselect } from "../../../../components/Multiselect";
import { contactOptions } from "../../QuestionPages/Questions/questionOptions";

// Custom questions
import renderBPcuffQuestion from "../questions/QuestionBloodPressureCuff";
import renderGlucometerQuestion from "../questions/QuestionGlucometer";
import renderStudyArmQuestion from "../questions/QuestionStudyArm";
import renderAddressQuestion from "../questions/QuestionAddress";

// API Response
import { ValidProviderProgramAPIResponse } from "../types";

// Static contact component for support contact
import { support_contact } from "../../../../res/support"


type Props = {
    provider: string | undefined;
    program: string | undefined;
    validProviderAndProgram: boolean;
    showRegistrationForm: boolean;
    apiResponse: ValidProviderProgramAPIResponse | null; // define this type appropriately

    selectedLanguage: string;
    setSelectedLanguage: (value: string) => void;
    validSelectedLanguage: boolean;

    firstNameValue: string;
    setFirstNameValue: (value: string) => void;
    validFirstName: boolean;

    lastNameValue: string;
    setLastNameValue: (value: string) => void;
    validLastName: boolean;

    phoneValue: string;
    setPhoneValue: (value: string) => void;
    validPhoneNumber: boolean;

    emailValue: string;
    setEmailValue: (value: string) => void;
    validEmail: boolean;

    preferredContactMethodValue: string[];
    setPreferredContactMethod: (value: string[]) => void;
    validPreferredContactMethod: boolean;

    bpCuffValue: string;
    setBpCuffValue: (value: string) => void;
    validBpCuff: boolean;

    glucometerValue: string;
    setGlucometerValue: (value: string) => void;
    validGlucometer: boolean;

    studyArmValue: string;
    setStudyArmValue: (value: string) => void;
    validStudyArm: boolean;

    addressStreetValue: string;
    setAddressStreetValue: (value: string) => void;
    validAddressStreet: boolean;
    addressCityValue: string;
    setAddressCityValue: (value: string) => void;
    validAddressCity: boolean;
    addressStateValue: string;
    setAddressStateValue: (value: string) => void;
    validAddressState: boolean;
    addressZipCodeValue: string;
    setAddressZipCodeValue: (value: string) => void;
    validAddressZipCode: boolean;

    statusMessage: JSX.Element | undefined;
    submit: () => void;
    submissionInProgress: boolean;
};

function RegistrationFormSingleProgram(props: Props) {
    const {
        provider,
        program,
        validProviderAndProgram,
        showRegistrationForm,
        apiResponse,
        selectedLanguage,
        setSelectedLanguage,
        validSelectedLanguage,
        firstNameValue,
        setFirstNameValue,
        validFirstName,
        lastNameValue,
        setLastNameValue,
        validLastName,
        phoneValue,
        setPhoneValue,
        validPhoneNumber,
        emailValue,
        setEmailValue,
        validEmail,
        preferredContactMethodValue,
        setPreferredContactMethod,
        validPreferredContactMethod,
        bpCuffValue,
        setBpCuffValue,
        validBpCuff,
        glucometerValue,
        setGlucometerValue,
        validGlucometer,
        studyArmValue,
        setStudyArmValue,
        validStudyArm,
        addressStreetValue,
        setAddressStreetValue,
        validAddressStreet,
        addressCityValue,
        setAddressCityValue,
        validAddressCity,
        addressStateValue,
        setAddressStateValue,
        validAddressState,
        addressZipCodeValue,
        setAddressZipCodeValue,
        validAddressZipCode,
        statusMessage,
        submit,
        submissionInProgress
    } = props;

    return (
        <div className="center-screen">
            <div className="glow-border page-container" style={{ maxWidth: "330px" }}>
                <SpaceBetween size='m' direction='vertical'>

                    {/* Optional logo from provider */}
                    {provider ? renderProviderLogo({ provider }) : null}

                    {/* Conditional title for program */}
                    {program && (program.split("_")[0].toUpperCase() === "HTN") &&
                        <h3 className='line-align-center' style={{ marginBottom: '0' }}>Hypertension Program Enrollment</h3>
                    }
                    {program && (program.split("_")[0].toUpperCase() === "DM") &&
                        <h3 className='line-align-center' style={{ marginBottom: '0' }}>Diabetes Program Enrollment</h3>
                    }

                    {/* Invalid program/provider message */}
                    {(!validProviderAndProgram) &&
                        <div>
                            <p>This is not a valid registration link.</p>
                            {support_contact}
                        </div>
                    }

                    {/* Registration form */}
                    {(showRegistrationForm) && (apiResponse) &&
                        <SpaceBetween size='m' direction='vertical'>
                            <DropdownBox
                                label="Select a Language"
                                options={apiResponse.supported_languages.map(language => ({ value: language.language_id, label: language.language_name }))}
                                selectedValue={selectedLanguage}
                                onChange={setSelectedLanguage}
                                showMessage={!validSelectedLanguage}
                            />

                            <SpaceBetween size='xxxs' direction='vertical'>
                                <div>Name</div>
                                <SpaceBetween size='xxxs' direction='horizontal'>
                                    <TextInput
                                        value={firstNameValue}
                                        onChange={setFirstNameValue}
                                        placeholder="First Name"
                                        showMessage={!validFirstName}
                                    />
                                    <TextInput
                                        value={lastNameValue}
                                        onChange={setLastNameValue}
                                        placeholder="Last Name"
                                        showMessage={!validLastName}
                                    />
                                </SpaceBetween>
                            </SpaceBetween>

                            <SpaceBetween size='m' direction='horizontal'>
                                <div>Phone Number</div>
                            </SpaceBetween>

                            <PhoneInput
                                country={'us'}
                                value={phoneValue}
                                onChange={setPhoneValue}
                                buttonStyle={{
                                    borderTopLeftRadius: '8px',
                                    borderBottomLeftRadius: '8px',
                                    borderTop: '2px solid #9ba7b6',
                                    borderBottom: '2px solid #9ba7b6',
                                    borderLeft: '2px solid #9ba7b6'
                                }}
                                inputStyle={{
                                    width: '100%',
                                    borderTopRightRadius: '8px',
                                    borderBottomRightRadius: '8px',
                                    borderTop: '2px solid #9ba7b6',
                                    borderBottom: '2px solid #9ba7b6',
                                    borderRight: '2px solid #9ba7b6'
                                }}
                                placeholder="Phone number"
                            />
                            {!validPhoneNumber && <p className='tip-text'>Please enter a valid phone number.</p>}

                            <TextInput
                                label="Email"
                                value={emailValue}
                                onChange={setEmailValue}
                                type="email"
                                placeholder="Email Address"
                                showMessage={!validEmail}
                                message="Please enter a valid email."
                            />

                            <Multiselect
                                label="Which contact methods do you prefer? (Select all that apply)"
                                options={contactOptions}
                                selectedValue={preferredContactMethodValue}
                                onChange={setPreferredContactMethod}
                                showMessage={!validPreferredContactMethod}
                            />

                            {renderBPcuffQuestion(
                                apiResponse.show_bp_cuff_question,
                                apiResponse.provide_bp_cuff_option,
                                apiResponse.bp_cuff_require_bluetooth_option,
                                bpCuffValue,
                                setBpCuffValue,
                                validBpCuff
                            )}

                            {renderGlucometerQuestion(
                                apiResponse.show_glucometer_question,
                                apiResponse.provide_glucometer_option,
                                apiResponse.finger_prick_option,
                                glucometerValue,
                                setGlucometerValue,
                                validGlucometer
                            )}

                            {renderStudyArmQuestion(
                                apiResponse.show_study_arm_question,
                                studyArmValue,
                                setStudyArmValue,
                                validStudyArm
                            )}

                            {renderAddressQuestion(
                                bpCuffValue,
                                glucometerValue,
                                addressStreetValue,
                                setAddressStreetValue,
                                validAddressStreet,
                                addressCityValue,
                                setAddressCityValue,
                                validAddressCity,
                                addressStateValue,
                                setAddressStateValue,
                                validAddressState,
                                addressZipCodeValue,
                                setAddressZipCodeValue,
                                validAddressZipCode
                            )}

                            <div className='line-align-center'>
                                <Button variant='primary' onClick={submit} disabled={submissionInProgress}>
                                    {submissionInProgress && <Spinner />}
                                    Submit
                                </Button>
                            </div>

                            <p className='tip-text' style={{ fontSize: '12px', textAlign: 'center', marginTop: '8px', color: 'black' }}>
                                By providing your phone number and opting in, you agree to receive SMS messages from CIPRA.ai related to your remote patient monitoring program, including reminders and care updates. Message frequency varies. Message and data rates may apply. Reply STOP to cancel or HELP for help.
                            </p>
                        </SpaceBetween>
                    }

                    {/* Status message after submission*/}
                    {statusMessage && <p className='tip-text'>{statusMessage}</p>}

                    {/* Branding footer */}
                    {provider ? renderPowerBy({ provider: provider?.toUpperCase() }) : null}
                </SpaceBetween>
            </div>
        </div>
    );
}

export default RegistrationFormSingleProgram;